(() => {
  const namespace = window.AuremeStaffstartDefaultOverrides;
  const runtime = namespace?.runtime;
  const coordinateIndex = namespace?.coordinateIndex;
  if (!runtime || !coordinateIndex) return;

  const {
    createEmptyMarkedElements,
    getTrimmedText,
    isCoordinateIndexPath,
    patchOrder,
    registerPatch,
    syncAttributeMarkers,
  } = runtime;
  const {
    attributes,
    collectElementsByExactAttribute,
    collectElementsByExactText,
    collectFilterBlocks,
    collectFilterHeadingElements,
    selectors,
    syncElementText,
    syncHashtagSearchPlaceholder,
    textMatchers,
    typeFilterTextReplacements,
    uiTextReplacements,
  } = coordinateIndex;

  /**
   * @typedef {object} CoordinateIndexTypographyTargets
   * @property {Set<HTMLElement>} filterHeadings - 絞り込み見出し marker 対象
   * @property {Set<HTMLElement>} hashtagHeadings - ハッシュタグ見出し marker 対象
   * @property {Set<HTMLElement>} hashtagSearchFields - ハッシュタグ検索欄 marker 対象
   */

  /**
   * タイプ絞り込み内の表示名変更対象を一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectTypeFilterTextReplacementElements(root) {
    const elements = new Set();
    const typeFilterBlocks = collectFilterBlocks(
      root,
      textMatchers.type,
      selectors.typeFilterControl,
    );

    for (const block of typeFilterBlocks) {
      for (const link of block.querySelectorAll('a[href*="genre="]')) {
        if (!(link instanceof HTMLAnchorElement)) continue;
        if (link.children.length > 0) continue;
        if (!textMatchers.typeFilter.has(getTrimmedText(link))) continue;

        elements.add(link);
      }

      for (const option of block.querySelectorAll('select option')) {
        if (!(option instanceof HTMLOptionElement)) continue;
        if (!option.hasAttribute('value')) continue;
        if (!textMatchers.typeFilter.has(getTrimmedText(option))) continue;

        elements.add(option);
      }
    }

    return elements;
  }

  /**
   * 現在の絞り込み条件内のタイプ表示名変更対象を一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectActiveTypeFilterTextReplacementElements(root) {
    const elements = new Set();
    const filterConditionLabels = collectElementsByExactText(
      root,
      'p, span, label',
      textMatchers.filterCondition,
    );

    for (const label of filterConditionLabels) {
      const conditionBlock = label.parentElement;
      if (!(conditionBlock instanceof HTMLElement)) continue;

      for (const link of conditionBlock.querySelectorAll('a')) {
        if (!(link instanceof HTMLAnchorElement)) continue;
        if (link.children.length > 0) continue;
        if (!textMatchers.typeFilter.has(getTrimmedText(link))) continue;

        elements.add(link);
      }
    }

    return elements;
  }

  /**
   * STAFF SNAP 一覧の表示文言同期対象を返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectCoordinateIndexTextReplacementElements(root) {
    return new Set([
      ...collectFilterHeadingElements(root),
      ...collectTypeFilterTextReplacementElements(root),
      ...collectActiveTypeFilterTextReplacementElements(root),
      ...collectElementsByExactText(root, 'h1, h2, h3', textMatchers.hashtag),
      ...collectElementsByExactText(root, 'p, span, label', textMatchers.filterCondition),
      ...collectElementsByExactAttribute(
        root,
        'input[placeholder], textarea[placeholder]',
        'placeholder',
        textMatchers.hashtagSearchPlaceholder,
      ),
    ]);
  }

  /**
   * STAFF SNAP 一覧の表示文言を同期する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<HTMLElement>} elements - 同期対象要素
   */
  function syncCoordinateIndexTextReplacements(root, elements) {
    for (const element of elements) {
      if (!(element instanceof HTMLElement)) continue;
      if (!root.contains(element)) continue;

      syncElementText(element, uiTextReplacements);
      syncElementText(element, typeFilterTextReplacements);
      syncHashtagSearchPlaceholder(element);
    }
  }

  /**
   * STAFF SNAP 一覧の文字指定 marker 用に空対象を返す。
   * @returns {CoordinateIndexTypographyTargets}
   */
  function createEmptyCoordinateIndexTypographyTargets() {
    return {
      filterHeadings: createEmptyMarkedElements(),
      hashtagHeadings: createEmptyMarkedElements(),
      hashtagSearchFields: createEmptyMarkedElements(),
    };
  }

  /**
   * STAFF SNAP 一覧の文字指定対象を返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {CoordinateIndexTypographyTargets}
   */
  function collectCoordinateIndexTypographyTargets(root) {
    return {
      filterHeadings: collectFilterHeadingElements(root),
      hashtagHeadings: collectElementsByExactText(
        root,
        'h1, h2, h3',
        textMatchers.hashtagTypography,
      ),
      hashtagSearchFields: collectElementsByExactAttribute(
        root,
        'input[placeholder], textarea[placeholder]',
        'placeholder',
        textMatchers.hashtagSearchTypographyPlaceholder,
      ),
    };
  }

  /**
   * STAFF SNAP 一覧の文字指定 marker を同期する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {CoordinateIndexTypographyTargets} elements - marker 対象
   */
  function syncCoordinateIndexTypographyMarkers(root, elements) {
    syncAttributeMarkers(root, attributes.filterHeading, elements.filterHeadings);
    syncAttributeMarkers(root, attributes.hashtagHeading, elements.hashtagHeadings);
    syncAttributeMarkers(root, attributes.hashtagSearch, elements.hashtagSearchFields);
  }

  registerPatch({
    order: patchOrder.coordinateIndex.textReplacement,
    isTargetRoute: isCoordinateIndexPath,
    collect: collectCoordinateIndexTextReplacementElements,
    createEmptyTargets: createEmptyMarkedElements,
    apply: syncCoordinateIndexTextReplacements,
  });

  registerPatch({
    order: patchOrder.coordinateIndex.typography,
    isTargetRoute: isCoordinateIndexPath,
    collect: collectCoordinateIndexTypographyTargets,
    createEmptyTargets: createEmptyCoordinateIndexTypographyTargets,
    apply: syncCoordinateIndexTypographyMarkers,
  });
})();
